export const SEASON_PICKS_MAX_SCORE = 20;

export const SEASON_PICKS_EMPTY_TITLE = 'No fixtures yet';
export const SEASON_PICKS_EMPTY_SUBTITLE = 'Matchweeks appear here as fixtures are scheduled.';
export const SEASON_PICKS_ERROR_TITLE = "Couldn't load fixtures";
export const SEASON_PICKS_ERROR_RETRY = 'Retry';
export const SEASON_PICKS_SAVE_ERROR = "Couldn't save — try again";
export const SEASON_PICKS_YOUR_PICK_PREFIX = 'Your pick';
export const SEASON_PICKS_LOCKS_AT_KICKOFF = 'locks at kickoff';

/**
 * Outcome vocabulary — deliberately the same words the WC finished card uses
 * (MATCH_CARD_COPY / matchCardOutcome.helper), so a user who learned the World Cup
 * reads a season pool without relearning anything (Slice 4 AC-12.2).
 */
export const SEASON_PICKS_OUTCOME_LABEL = {
  exact: 'Exact',
  result: 'Result',
  miss: 'Miss',
} as const;
export const SEASON_PICKS_SCORING = 'Scoring…';
export const SEASON_PICKS_NO_PICK = 'No pick';

/** Golden Ball control (tournament formats only; season pools have no boost). */
export const SEASON_PICKS_BOOST_ON = 'Double points on this match, on';
export const SEASON_PICKS_BOOST_OFF = 'Double points on this match';
/** This round's x2 is already sitting on another match (AC-12.1). */
export const SEASON_PICKS_BOOST_TAKEN = "Double points already used in this round";

/**
 * Live match chrome (Story 14). The minute rides on `liveMinute`, which the matches payload
 * already carries; before this the card showed a live match as an ordinary locked one.
 */
const SEASON_PICKS_LIVE = 'LIVE';
export const liveMinuteLabel = (minute: number | null): string =>
  (minute != null ? `${SEASON_PICKS_LIVE} ${minute}'` : SEASON_PICKS_LIVE);
/** Marks a settled pick that carried the x2, so the doubling stays legible after the fact. */
export const SEASON_PICKS_BOOSTED_MARK = '×2';
export const SEASON_PICKS_BOOSTED_A11Y = 'Doubled by your x2';

/**
 * Card action row (specs/champions-league AC-10.1). Same two labels the World Cup's MatchCard
 * uses, so a player moving between competitions reads the same buttons.
 */
export const SEASON_PICKS_ACTION_STATS = 'Stats';
export const SEASON_PICKS_ACTION_FIXTURE = 'Match Details';

export const SEASON_PICKS_PREV_MATCHWEEK = 'Previous matchweek';
export const SEASON_PICKS_NEXT_MATCHWEEK = 'Next matchweek';

export const SEASON_PICKS_TEST_IDS = {
  screen: 'season-picks',
  chip: (round: string) => `season-picks-chip-${round}`,
  card: (matchId: string) => `season-pick-card-${matchId}`,
  stepper: (matchId: string, side: 'home' | 'away', op: 'plus' | 'minus') =>
    `season-pick-${matchId}-${side}-${op}`,
  boost: (matchId: string) => `season-pick-${matchId}-boost`,
  /** The 90-minute / penalties line, present only past 90 minutes (AC-14.6). */
  beyondNinety: (matchId: string) => `season-pick-${matchId}-beyond-ninety`,
  /** The tie aggregate, present only on a second leg (AC-6.9). */
  aggregate: (matchId: string) => `season-pick-${matchId}-aggregate`,
  /** Meta-row zones (AC-16.5), so the x2's placement is assertable and not just eyeballed. */
  metaLeading: (matchId: string) => `season-pick-${matchId}-meta-leading`,
  metaCenter: (matchId: string) => `season-pick-${matchId}-meta-center`,
  metaTrailing: (matchId: string) => `season-pick-${matchId}-meta-trailing`,
  live: (matchId: string) => `season-pick-${matchId}-live`,
  boostedMark: (matchId: string) => `season-pick-${matchId}-boosted`,
  stats: (matchId: string) => `season-pick-${matchId}-stats`,
  fixture: (matchId: string) => `season-pick-${matchId}-fixture`,
  retry: 'season-picks-retry',
  back: 'season-picks-back',
  prevMatchweek: 'season-picks-mw-prev',
  nextMatchweek: 'season-picks-mw-next',
} as const;
